import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Calendar, 
  Clock, 
  Plus, 
  Upload, 
  Download,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  User,
  Users,
  FileSpreadsheet
} from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays, startOfWeek, endOfWeek, addWeeks, subWeeks, parseISO } from 'date-fns';

const SHIFT_TYPES = [
  { value: 'regular', label: 'Regular' },
  { value: 'morning', label: 'Morning Shift' },
  { value: 'afternoon', label: 'Afternoon Shift' },
  { value: 'evening', label: 'Evening Shift' },
  { value: 'split', label: 'Split Shift' }
];

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function RotaManagementPage() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [rotaData, setRotaData] = useState({ entries: [], by_date: {} });
  const [staff, setStaff] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [mySchedule, setMySchedule] = useState([]);
  
  const [newEntry, setNewEntry] = useState({
    staff_id: '',
    staff_name: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    start_time: '09:00',
    end_time: '17:00',
    shift_type: 'regular',
    notes: ''
  });

  const isSuperAdmin = user?.email?.toLowerCase() === 'admin@icrcl.co.uk';
  const isAdmin = isSuperAdmin || ['director', 'managing_director', 'admin', 'hr', 'clinical_lead'].includes(user?.role);

  useEffect(() => {
    fetchData();
  }, [weekStart]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      
      if (isAdmin) {
        const [rotaRes, staffRes] = await Promise.all([
          api.get(`/rota/week?week_start=${weekStartStr}`),
          api.get('/rota/staff-list')
        ]);
        setRotaData(rotaRes.data);
        setStaff(staffRes.data.staff || []);
      }
      
      // Get my own schedule
      const myRes = await api.get('/rota/my-schedule');
      setMySchedule(myRes.data.schedule || []);
    } catch (error) {
      console.error('Failed to fetch rota data:', error);
    } finally {
      setLoading(false);
    }
  };

  const addRotaEntry = async () => {
    if (!newEntry.staff_id || !newEntry.date) {
      toast.error('Please select staff and date');
      return;
    }
    
    try {
      // Get staff name if not set
      const selectedStaff = staff.find(s => s.id === newEntry.staff_id);
      const entryWithName = {
        ...newEntry,
        staff_name: `${selectedStaff?.first_name || ''} ${selectedStaff?.last_name || ''}`.trim()
      };
      
      await api.post('/rota/entry', entryWithName);
      toast.success('Rota entry added');
      setShowAddDialog(false);
      fetchData();
      setNewEntry({
        staff_id: '',
        staff_name: '',
        date: format(new Date(), 'yyyy-MM-dd'),
        start_time: '09:00',
        end_time: '17:00',
        shift_type: 'regular',
        notes: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add entry');
    }
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm('Delete this rota entry?')) return;
    
    try {
      await api.delete(`/rota/entry/${entryId}`);
      toast.success('Entry deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/rota/upload-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Uploaded ${response.data.entries_added} entries`);
      if (response.data.errors?.length) {
        toast.warning(`${response.data.errors.length} rows had errors`);
      }
      
      setShowUploadDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await api.get('/rota/template');
      const blob = new Blob([response.data.template], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'rota_template.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const getWeekDates = () => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      dates.push(addDays(weekStart, i));
    }
    return dates;
  };

  const getEntriesForDate = (dateStr) => {
    return rotaData.by_date?.[dateStr] || [];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f] flex items-center gap-3">
            <Calendar className="w-8 h-8 text-[#2a9d8f]" />
            Rota Management
          </h1>
          <p className="text-slate-500 mt-1">Manage staff schedules and shift rotas</p>
        </div>
        
        {isAdmin && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowUploadDialog(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Upload CSV
            </Button>
            <Button onClick={() => setShowAddDialog(true)} className="bg-[#1e3a5f]" data-testid="add-rota-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Entry
            </Button>
          </div>
        )}
      </div>

      <Tabs defaultValue={isAdmin ? "weekly" : "my-schedule"} className="w-full">
        <TabsList>
          <TabsTrigger value="my-schedule">My Schedule</TabsTrigger>
          {isAdmin && <TabsTrigger value="weekly">Weekly Rota</TabsTrigger>}
        </TabsList>
        
        {/* My Schedule Tab */}
        <TabsContent value="my-schedule" className="mt-4">
          <Card className="card-icrcl">
            <CardHeader>
              <CardTitle className="text-lg text-[#1e3a5f]">My Upcoming Shifts</CardTitle>
              <CardDescription>Your scheduled work times</CardDescription>
            </CardHeader>
            <CardContent>
              {mySchedule.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Clock className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No shifts scheduled for this period</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {mySchedule.map((entry, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#1e3a5f] text-white rounded-lg flex flex-col items-center justify-center">
                          <span className="text-xs">{format(parseISO(entry.date), 'EEE')}</span>
                          <span className="text-lg font-bold">{format(parseISO(entry.date), 'd')}</span>
                        </div>
                        <div>
                          <p className="font-medium">{format(parseISO(entry.date), 'EEEE, MMMM d, yyyy')}</p>
                          <p className="text-sm text-slate-500">
                            {entry.start_time} - {entry.end_time}
                          </p>
                        </div>
                      </div>
                      <Badge className="bg-[#2a9d8f]/10 text-[#2a9d8f]">
                        {entry.shift_type}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Weekly Rota Tab (Admin only) */}
        {isAdmin && (
          <TabsContent value="weekly" className="mt-4">
            <Card className="card-icrcl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg text-[#1e3a5f]">Weekly Schedule</CardTitle>
                    <CardDescription>
                      Week of {format(weekStart, 'MMMM d, yyyy')}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setWeekStart(subWeeks(weekStart, 1))}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}
                    >
                      Today
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setWeekStart(addWeeks(weekStart, 1))}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <div className="grid grid-cols-7 gap-2 min-w-[900px]">
                    {getWeekDates().map((date, i) => {
                      const dateStr = format(date, 'yyyy-MM-dd');
                      const entries = getEntriesForDate(dateStr);
                      const isToday = format(new Date(), 'yyyy-MM-dd') === dateStr;
                      
                      return (
                        <div
                          key={dateStr}
                          className={`border rounded-lg p-3 min-h-[200px] ${
                            isToday ? 'border-[#2a9d8f] bg-[#2a9d8f]/5' : 'border-slate-200'
                          }`}
                        >
                          <div className="text-center mb-3">
                            <p className="text-sm font-medium text-slate-500">{DAYS_OF_WEEK[i]}</p>
                            <p className={`text-2xl font-bold ${isToday ? 'text-[#2a9d8f]' : 'text-[#1e3a5f]'}`}>
                              {format(date, 'd')}
                            </p>
                          </div>
                          
                          <div className="space-y-2">
                            {entries.map((entry) => (
                              <div
                                key={entry.id}
                                className="bg-white border border-slate-200 rounded p-2 text-xs group relative"
                              >
                                <p className="font-medium truncate">{entry.staff_name}</p>
                                <p className="text-slate-500">{entry.start_time} - {entry.end_time}</p>
                                <Badge className="mt-1 text-[10px]">{entry.shift_type}</Badge>
                                
                                <button
                                  onClick={() => deleteEntry(entry.id)}
                                  className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <Trash2 className="w-3 h-3 text-red-500" />
                                </button>
                              </div>
                            ))}
                            
                            {entries.length === 0 && (
                              <p className="text-xs text-slate-400 text-center">No shifts</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Add Entry Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Rota Entry</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Staff Member *</Label>
              <Select
                value={newEntry.staff_id}
                onValueChange={(v) => setNewEntry({ ...newEntry, staff_id: v })}
              >
                <SelectTrigger data-testid="rota-staff-select">
                  <SelectValue placeholder="Select staff..." />
                </SelectTrigger>
                <SelectContent>
                  {staff.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.first_name} {s.last_name} ({s.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={newEntry.date}
                onChange={(e) => setNewEntry({ ...newEntry, date: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time *</Label>
                <Input
                  type="time"
                  value={newEntry.start_time}
                  onChange={(e) => setNewEntry({ ...newEntry, start_time: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>End Time *</Label>
                <Input
                  type="time"
                  value={newEntry.end_time}
                  onChange={(e) => setNewEntry({ ...newEntry, end_time: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Shift Type</Label>
              <Select
                value={newEntry.shift_type}
                onValueChange={(v) => setNewEntry({ ...newEntry, shift_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SHIFT_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Input
                value={newEntry.notes}
                onChange={(e) => setNewEntry({ ...newEntry, notes: e.target.value })}
                placeholder="Any additional notes..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={addRotaEntry} className="bg-[#1e3a5f]">Add Entry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Rota CSV</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Upload a CSV file with columns: staff_id, staff_name, date, start_time, end_time, shift_type, notes
            </p>
            
            <Button variant="outline" onClick={downloadTemplate} className="w-full">
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
            
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 text-slate-400" />
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
                id="csv-upload"
              />
              <label
                htmlFor="csv-upload"
                className="cursor-pointer text-[#1e3a5f] hover:underline"
              >
                Click to select CSV file
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
